import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  MenuItem,
  Paper,
  Popper,
  TextField,
  useTheme,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomSelect from "../../../components/ui/Select";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import { BookingData } from "../../../types/bookings";
import { initialValues } from "../../bookings/AddBooking";
import { buildBookingFormData } from "../../bookings/utils/add-booking-form-data";
import DatePicker from "../../proposal/service-quote-form/components/DatePicker";
import TimePicker from "../../proposal/service-quote-form/components/TimePicker";
import BookingInputCard from "./BookingInputCard";
import QuickBookingHeader from "./QuickBookingHeader";

const BOOKING_TYPES = [
  { label: "Funeral", value: "FUNERAL" },
  { label: "Event", value: "EVENT" },
  { label: "Conference", value: "CONFERENCE" },
  { label: "Church", value: "CHURCH" },
  { label: "Funeral Home", value: "FUNERAL_HOME" },
  { label: "Other", value: "OTHER" },
];

interface QuickInfoState {
  deceased_name: string;
  deceased_surname: string;
  type_of_service: string;
  church_name: string;
  conference_name: string;
  conference_type: string;
  end_date: string;
  end_time: string;
  event_name: string;
  title: string;
}

const initialQuickInfo: QuickInfoState = {
  deceased_name: "",
  deceased_surname: "",
  type_of_service: "",
  church_name: "",
  conference_name: "",
  conference_type: "",
  end_date: "",
  end_time: "",
  event_name: "",
  title: "",
};

interface CreateBookingFormProps {
  buttonColor?: string;
}

const CreateBookingForm: React.FC<CreateBookingFormProps> = ({
  buttonColor,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [when, setWhen] = useState("");
  const [bookingType, setBookingType] = useState("");
  const [whatTime, setWhatTime] = useState("");

  const [whenAnchor, setWhenAnchor] = useState<HTMLElement | null>(null);
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);
  const [timeAnchor, setTimeAnchor] = useState<HTMLElement | null>(null);

  const typeBoxRef = useRef<HTMLDivElement | null>(null);
  const timeBoxRef = useRef<HTMLDivElement | null>(null);

  const [quickInfoOpen, setQuickInfoOpen] = useState(false);
  const [quickInfo, setQuickInfo] = useState<QuickInfoState>(initialQuickInfo);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const formatDate = (date: string) => {
    if (!date) return "Add date";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    if (!time) return "Add time";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatType = (type: string) => {
    if (!type) return "Select type";
    return BOOKING_TYPES.find((t) => t.value === type)?.label || type;
  };

  const handleCreateBooking = () => {
    if (!when || !bookingType || !whatTime) {
      toast.error("Please fill in all fields.");
      return;
    }
    setQuickInfo(initialQuickInfo);
    setQuickInfoOpen(true);
  };

  const buildPrefilledData = (): BookingData => ({
    ...initialValues,
    booking_type: bookingType,
    date_of_service: when,
    start_time: whatTime,
    end_time: quickInfo.end_time || undefined,
    venue_ids: [],
    venue: undefined,

    deceased_name: quickInfo.deceased_name || undefined,
    deceased_surname: quickInfo.deceased_surname || undefined,
    type_of_service: quickInfo.type_of_service || null,

    church_name: quickInfo.church_name || undefined,

    title: quickInfo.conference_name || quickInfo.title || undefined,
    conference_type: quickInfo.conference_type || null,
    start_date_of_event: when,
    end_date_of_event: quickInfo.end_date || null,

    event_name: quickInfo.event_name || null,
  });

  const handleSaveMinimal = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const data = buildPrefilledData();
      const payload = buildBookingFormData(data);
      await apiService.post(
        "/api/bookings/booking/create-fullbooking/",
        payload,
      );
      toast.success("Booking created successfully");
      setQuickInfoOpen(false);
      setWhen("");
      setBookingType("");
      setWhatTime("");
    } catch {
      toast.error("Failed to create booking");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddFullDetails = () => {
    const data = buildPrefilledData();
    setQuickInfoOpen(false);

    const pathSlug = bookingType?.toLowerCase().replace("_", "-") || "funeral";

    navigate(`/bookings/add/${pathSlug}`, {
      state: {
        serviceType: bookingType,
        defaultBookingType: bookingType,
        prefillData: data,
      },
    });
  };

  const isFuneral = ["FUNERAL", "FUNERAL_HOME"].includes(bookingType);
  const isChurch = bookingType === "CHURCH";
  const isConference = bookingType === "CONFERENCE";
  const isEvent = bookingType === "EVENT";
  const isOther = bookingType === "OTHER";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      "& fieldset": { borderColor: colors.primary[300] },
      "&:hover fieldset": { borderColor: colors.primary[400] },
      "&.Mui-focused fieldset": { borderColor: colors.primary[400] },
    },
    "& .MuiInputLabel-root": { color: colors.primary[300] },
    "& .MuiInputBase-input": { color: colors.neutral[100] },
  };

  return (
    <Box>
      <BookingInputCard
        when={when}
        bookingType={bookingType}
        whatTime={whatTime}
        typeBoxRef={typeBoxRef}
        timeBoxRef={timeBoxRef}
        onWhenClick={(el) => setWhenAnchor(el)}
        onTypeClick={(el) => setTypeAnchor(el)}
        onTimeClick={(el) => setTimeAnchor(el)}
        onCreateBooking={handleCreateBooking}
        formatDate={formatDate}
        formatTime={formatTime}
        formatType={formatType}
        buttonColor={buttonColor}
      />

      {/* Date Picker */}
      <DatePicker
        open={Boolean(whenAnchor)}
        anchorEl={whenAnchor}
        onClose={() => setWhenAnchor(null)}
        value={when}
        onChange={(date) => {
          setWhen(date);
          setWhenAnchor(null);
          if (typeBoxRef.current) setTypeAnchor(typeBoxRef.current);
        }}
        disablePast={true}
        showFlexibleDates={true}
      />

      {/* Booking Type Picker */}
      <Popper
        open={Boolean(typeAnchor)}
        anchorEl={typeAnchor}
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setTypeAnchor(null)}>
          <Paper elevation={8} sx={{ py: 1, borderRadius: 3, minWidth: 180 }}>
            {BOOKING_TYPES.map((type) => (
              <Box
                key={type.value}
                onClick={() => {
                  setBookingType(type.value);
                  setTypeAnchor(null);
                  if (timeBoxRef.current) setTimeAnchor(timeBoxRef.current);
                }}
                sx={{
                  px: 2.5,
                  py: 1.2,
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: bookingType === type.value ? 600 : 400,
                  backgroundColor:
                    bookingType === type.value
                      ? "action.selected"
                      : "transparent",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                {type.label}
              </Box>
            ))}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Time Picker */}
      <TimePicker
        open={Boolean(timeAnchor)}
        anchorEl={timeAnchor}
        onClose={() => setTimeAnchor(null)}
        value={whatTime}
        onChange={(time) => {
          setWhatTime(time);
          setTimeAnchor(null);
        }}
        format="12h"
      />

      <Dialog
        open={quickInfoOpen}
        onClose={() => setQuickInfoOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <QuickBookingHeader
          bookingType={formatType(bookingType)}
        />
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "20px !important",
          }}
        >
          {/* FUNERAL / FUNERAL_HOME */}
          {isFuneral && (
            <>
              <TextField
                label="Deceased First Name"
                fullWidth
                sx={fieldSx}
                value={quickInfo.deceased_name}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, deceased_name: e.target.value }))
                }
              />
              <TextField
                label="Deceased Surname"
                fullWidth
                sx={fieldSx}
                value={quickInfo.deceased_surname}
                onChange={(e) =>
                  setQuickInfo((p) => ({
                    ...p,
                    deceased_surname: e.target.value,
                  }))
                }
              />
              <FormControl fullWidth>
                <CustomSelect
                  label="Type of Service"
                  value={quickInfo.type_of_service}
                  onChange={(e) =>
                    setQuickInfo((p) => ({
                      ...p,
                      type_of_service: e.target.value as string,
                    }))
                  }
                >
                  <MenuItem value="Funeral">Funeral</MenuItem>
                  <MenuItem value="Celebration">Celebration of Life</MenuItem>
                  <MenuItem value="Burial">Burial</MenuItem>
                </CustomSelect>
              </FormControl>
              <TextField
                label="End Time"
                fullWidth
                sx={fieldSx}
                type="time"
                InputLabelProps={{ shrink: true }}
                value={quickInfo.end_time}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, end_time: e.target.value }))
                }
              />
            </>
          )}

          {/* CHURCH */}
          {isChurch && (
            <>
              <TextField
                label="Church Name"
                fullWidth
                sx={fieldSx}
                value={quickInfo.church_name}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, church_name: e.target.value }))
                }
              />
              <TextField
                label="End Time"
                fullWidth
                sx={fieldSx}
                type="time"
                InputLabelProps={{ shrink: true }}
                value={quickInfo.end_time}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, end_time: e.target.value }))
                }
              />
            </>
          )}

          {/* CONFERENCE */}
          {isConference && (
            <>
              <TextField
                label="Conference Name"
                fullWidth
                sx={fieldSx}
                value={quickInfo.conference_name}
                onChange={(e) =>
                  setQuickInfo((p) => ({
                    ...p,
                    conference_name: e.target.value,
                  }))
                }
              />
              <FormControl fullWidth>
                <CustomSelect
                  label="Conference Type"
                  value={quickInfo.conference_type}
                  onChange={(e) =>
                    setQuickInfo((p) => ({
                      ...p,
                      conference_type: e.target.value as string,
                    }))
                  }
                >
                  <MenuItem value="summit">Summit</MenuItem>
                  <MenuItem value="workshop">Workshop</MenuItem>
                  <MenuItem value="symposium">Symposium</MenuItem>
                  <MenuItem value="dinner">Dinner</MenuItem>
                  <MenuItem value="breakfast_meeting">
                    Breakfast Meeting
                  </MenuItem>
                  <MenuItem value="congress">Congress</MenuItem>
                  <MenuItem value="conference">Conference</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </CustomSelect>
              </FormControl>
              <TextField
                label="End Date"
                fullWidth
                sx={fieldSx}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={quickInfo.end_date}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, end_date: e.target.value }))
                }
              />
              <TextField
                label="End Time"
                fullWidth
                sx={fieldSx}
                type="time"
                InputLabelProps={{ shrink: true }}
                value={quickInfo.end_time}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, end_time: e.target.value }))
                }
              />
            </>
          )}

          {/* EVENT */}
          {isEvent && (
            <>
              <TextField
                label="Event Name"
                fullWidth
                sx={fieldSx}
                value={quickInfo.event_name}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, event_name: e.target.value }))
                }
              />
              <TextField
                label="End Time"
                fullWidth
                sx={fieldSx}
                type="time"
                InputLabelProps={{ shrink: true }}
                value={quickInfo.end_time}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, end_time: e.target.value }))
                }
              />
            </>
          )}

          {/* OTHER */}
          {isOther && (
            <>
              <TextField
                label="Title"
                fullWidth
                sx={fieldSx}
                value={quickInfo.title}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, title: e.target.value }))
                }
              />
              <TextField
                label="End Time"
                fullWidth
                sx={fieldSx}
                type="time"
                InputLabelProps={{ shrink: true }}
                value={quickInfo.end_time}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, end_time: e.target.value }))
                }
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleSaveMinimal}
            disabled={isSubmitting}
            sx={{ flex: 1, borderRadius: 3, textTransform: "none" }}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddFullDetails}
            sx={{
              flex: 1,
              borderRadius: 3,
              textTransform: "none",
              backgroundColor: buttonColor,
              "&:hover": { backgroundColor: buttonColor, opacity: 0.9 },
            }}
          >
            Add Full Details
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateBookingForm;
